import React, { useState } from 'react';
import { Eye, FileText, Loader2, Pencil, Send, User } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { sendInvoiceApi } from '../../api/invoice/sendInvoice';
import { Invoice } from '../../types/invoice';
import { BtnPrimary } from './Buttons';
import Modal from './Modal';

interface InvoiceCardProps {
  invoice: Invoice;
  onEdit?: (invoice: Invoice) => void;
  getStatusColor?: (status: string) => string;
  onSent?: () => void;
  selected?: boolean;
  onToggleSelect?: (invoiceId: string) => void;
}

const backendUrl = import.meta.env.VITE_CHOCO_URL;

const InvoiceCard: React.FC<InvoiceCardProps> = ({
  invoice,
  onEdit,
  getStatusColor,
  onSent,
  selected = false,
  onToggleSelect,
}) => {
  const [sending, setSending] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [emailEditable, setEmailEditable] = useState(false);

  const statusColor = getStatusColor?.(
    invoice?.paymentStatus || invoice?.status || ''
  );

  const handlePdfPreview = () => {
    window.open(
      `${backendUrl}/invoice/pdf/preview/${invoice?.invoiceFor}/${invoice?.id}`,
      '_blank'
    );
  };

  const openSendModal = () => {
    setSendEmail(invoice.billTo?.email || '');
    setEmailEditable(false);
    setSendModalOpen(true);
  };

  const handleConfirmSend = async () => {
    const alreadySent = !!invoice.lastSentAt;
    setSending(true);
    try {
      const result = await sendInvoiceApi(invoice.id!, alreadySent, sendEmail);
      setSendModalOpen(false);
      toast.success(result.message || 'Invoice sent');
      onSent?.();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to send invoice'
      );
    } finally {
      setSending(false);
    }
  };

  const alreadySent = !!invoice.lastSentAt;

  return (
    <>
    <Modal
      isOpen={sendModalOpen}
      onRequestClose={() => setSendModalOpen(false)}
      title={alreadySent ? 'Resend Invoice' : 'Send Invoice'}
      width="400px"
    >
      <div className="flex flex-col gap-4">
        {alreadySent && (
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Already sent on {new Date(invoice.lastSentAt!).toLocaleString()}
          </p>
        )}
        <div className="flex flex-col gap-1">
          <label className="text-sm text-zinc-600 dark:text-zinc-300">
            Confirm Email
          </label>
          <div className="relative">
            <input
              type="email"
              value={sendEmail}
              onChange={(e) => setSendEmail(e.target.value)}
              disabled={!emailEditable}
              className="w-full border border-zinc-300 dark:border-zinc-600 rounded-lg px-3 py-2 pr-9 text-sm bg-white dark:bg-zinc-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-zinc-50 disabled:dark:bg-zinc-900 disabled:text-zinc-700 disabled:dark:text-zinc-300 disabled:cursor-default"
            />
            {!emailEditable && (
              <button
                onClick={() => setEmailEditable(true)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                title="Edit email"
              >
                <Pencil size={14} />
              </button>
            )}
          </div>
        </div>
        <div className="flex justify-end">
          <BtnPrimary
            onClick={handleConfirmSend}
            disabled={!sendEmail.trim() || sending}
            className="flex items-center gap-2 disabled:opacity-50"
          >
            {sending && <Loader2 size={14} className="animate-spin" />}
            {alreadySent ? 'Resend Invoice' : 'Send Invoice'}
          </BtnPrimary>
        </div>
      </div>
    </Modal>
    <div className="bg-white dark:bg-zinc-900 border border-greyFifty dark:border-zinc-700 rounded-xl cursor-pointer shadow-sm px-4 pt-4 pb-2 transition hover:shadow-md flex flex-col justify-between">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {invoice.invoiceFor === 'agent' && onToggleSelect && (
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onToggleSelect(invoice.id!)}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 accent-blue-600 cursor-pointer"
            />
          )}
          <div className="text-base font-semibold text-blackFourty dark:text-white">
            {invoice.invoiceNumber}
          </div>
        </div>
        <span
          className={`text-xs font-medium px-3 py-1 rounded-lg border ${statusColor}`}
        >
          {(invoice.paymentStatus || invoice.status)?.toUpperCase()}
        </span>
      </div>

      {/* Bill To */}
      <div className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-300 my-2">
        <User className="w-4 h-4" />
        <span>{invoice.billTo?.name}</span>
      </div>

      {/* Main Info Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <div className="flex items-center gap-2">
          Billed{' '}
          <p className="text-sm font-semibold text-zinc-800 dark:text-white">
            {/* {data.currency} {data.totalAmount} */}
            {invoice.displayAmount}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="text-xs text-zinc-500 dark:text-zinc-400">
          <p>Service Period {invoice.servicePeriod?.display}</p>
          {invoice.lastSentAt && (
            <p className="text-green-600 dark:text-green-400">
              Sent {new Date(invoice.lastSentAt).toLocaleDateString()}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {onEdit && (
            <div className="flex items-center gap-1 justify-end">
              {invoice.invoiceFor === 'agent' && onSent && (
                <button
                  onClick={openSendModal}
                  disabled={sending}
                  title="Send invoice to agent"
                  className="p-1 rounded-md text-blackTen hover:bg-greyTwenty dark:text-blue-400 hover:underline disabled:opacity-50"
                >
                  {sending ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Send size={14} />
                  )}
                </button>
              )}
              <button
                onClick={handlePdfPreview}
                className="p-1 rounded-md text-blackTen hover:bg-greyTwenty dark:text-blue-400 hover:underline"
              >
                <FileText size={14} />
              </button>
              <button
                onClick={() => onEdit?.(invoice)}
                className="p-1 rounded-md text-blackTen hover:bg-greyTwenty dark:text-blue-400 hover:underline"
              >
                <Eye size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default InvoiceCard;
